import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useInstanceMode } from '../../providers/InstanceModeProvider';
import { useLabels } from '../../providers/LabelsProvider';
import { useT } from '../../providers/I18nProvider';

export default function Sidebar({ isOpen, onClose, collapsed, onToggleCollapse }) {
  const labels = useLabels();
  const t = useT();
  const location = useLocation();
  const role = localStorage.getItem('role') || 'user';
  const isAdmin = role === 'global_admin' || role === 'assessment_admin' || role === 'admin';
  const { pureOES } = useInstanceMode();

  const isActive = (path) => {
    // Exact match for root paths
    if (location.pathname === path) return true;
    // For paths with sub-routes, check startsWith but exclude paths like /admin that match all subroutes
    if (path === '/admin') return location.pathname === path; // /admin only matches /admin exactly
    if (path === '/dashboard') return location.pathname === path; // /dashboard only matches exactly
    // For other paths like /oes, allow sub-route matching
    return location.pathname.startsWith(path + '/');
  };

  // Minimalist SVG icons (matching Admin Dashboard)
  const iconMap = {
    dashboard: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
    results: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m7 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    assess: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>,
    oes: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>,
    org: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>,
    users: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>,
    invite: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
    lock: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
    logs: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" /></svg>,
    reports: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>,

    settings: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    profile: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
    admin: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>,
  };

  const menuItem = ({ to, label, icon, badge, requiredRole = null }) => {
    if (requiredRole && !requiredRole.includes(role)) return null;

    const active = isActive(to);
    return (
      <Link
        key={to}
        to={to}
        onClick={onClose}
        className={`flex items-center gap-2 px-3 py-3 rounded-lg transition-colors ${collapsed ? 'md:justify-center' : ''} ${
          active
            ? 'bg-eucnet-blue text-white'
            : 'text-eucnet-grey-700 hover:bg-eucnet-grey-100'
        }`}
        title={collapsed ? label : ''}
      >
        <span className="flex-shrink-0">{iconMap[icon]}</span>
        {!collapsed && <span className="font-medium whitespace-nowrap">{label}</span>}
        {!collapsed && badge && (
          <span className="ml-auto px-2 py-1 text-xs font-bold bg-red-500 text-white rounded-full">
            {badge}
          </span>
        )}
      </Link>
    );
  };

  return (
    <>
      {/* Backdrop - closes sidebar on mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-[80px] h-[calc(100vh-80px)] z-20 bg-white shadow-lg transition-all duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } md:translate-x-0 ${collapsed ? 'md:w-20' : 'md:w-64'} w-64`}
      >
        <div className="h-full flex flex-col">
          {/* Collapse button */}
          <div className="hidden md:flex items-center justify-end p-2 border-b border-eucnet-grey-200">
            <button
              onClick={onToggleCollapse}
              className="p-2 hover:bg-eucnet-grey-100 rounded-lg transition-colors"
              title={collapsed ? t('nav.expand') : t('nav.collapse')}
            >
              <svg className="w-5 h-5 text-eucnet-grey-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {collapsed ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5l-7 7 7 7" />
                )}
              </svg>
            </button>
          </div>

          {/* Menu items */}
          <div className="p-2 space-y-1">
          {/* User Menu Section */}
          <div className={`mb-4 pb-4 border-b border-eucnet-grey-200 ${collapsed ? 'space-y-1' : 'space-y-0'}`}>
            {menuItem({
              to: '/dashboard',
              label: t('nav.dashboard'),
              icon: 'dashboard',
              requiredRole: null,
            })}
          </div>

          {/* Main Navigation */}
          <div className={`${collapsed ? 'space-y-1' : 'space-y-0'}`}>
            <p className={`px-3 py-2 text-xs font-bold text-eucnet-grey-500 uppercase tracking-wider ${collapsed ? 'md:hidden' : ''}`}>
              {t('nav.assessments')}
            </p>
            {menuItem({
              to: '/admin/assessment-results',
              label: t('nav.assessmentResults'),
              icon: 'results',
              requiredRole: ['global_admin', 'assessment_admin', 'admin'],
            })}
            {menuItem({
              to: '/admin/assessments',
              label: t('nav.manageAssessments'),
              icon: 'assess',
              requiredRole: ['global_admin', 'assessment_admin', 'admin'],
            })}
          </div>

          {/* OES Management — coordinator only */}
          {!pureOES && (
            <div className={`${collapsed ? 'space-y-1' : 'space-y-0'} ${collapsed ? 'mt-3' : 'mt-2'}`}>
              <p className={`px-3 py-2 text-xs font-bold text-eucnet-grey-500 uppercase tracking-wider ${collapsed ? 'md:hidden' : ''}`}>
                {labels.entityName}
              </p>
              {menuItem({
                to: '/oes',
                label: `${labels.entityName} Management`,
                icon: 'oes',
                requiredRole: ['global_admin', 'assessment_admin', 'admin'],
              })}
            </div>
          )}

          {/* Admin Section — coordinator only */}
          {isAdmin && !pureOES && (
            <div className={`${collapsed ? 'space-y-1' : 'space-y-0'} ${collapsed ? 'mt-3' : 'mt-2'}`}>
              <p className={`px-3 py-2 text-xs font-bold text-eucnet-grey-500 uppercase tracking-wider ${collapsed ? 'md:hidden' : ''}`}>
                {t('nav.administration')}
              </p>
              {menuItem({
                to: '/admin',
                label: t('nav.adminDashboard'),
                icon: 'admin',
                requiredRole: ['global_admin', 'assessment_admin', 'admin'],
              })}
            </div>
          )}

          {/* Management — OES mode: direct access to Users */}
          {pureOES && isAdmin && (
            <div className={`${collapsed ? 'space-y-1' : 'space-y-0'} ${collapsed ? 'mt-3' : 'mt-2'}`}>
              <p className={`px-3 py-2 text-xs font-bold text-eucnet-grey-500 uppercase tracking-wider ${collapsed ? 'md:hidden' : ''}`}>
                {t('nav.management')}
              </p>
              {menuItem({
                to: '/admin/users',
                label: t('nav.users'),
                icon: 'users',
                requiredRole: ['global_admin', 'admin'],
              })}
            </div>
          )}

          {/* Reports */}
          <div className={`${collapsed ? 'space-y-1' : 'space-y-0'} ${collapsed ? 'mt-3' : 'mt-2'}`}>
            <p className={`px-3 py-2 text-xs font-bold text-eucnet-grey-500 uppercase tracking-wider ${collapsed ? 'md:hidden' : ''}`}>
              {t('nav.insights')}
            </p>
            {menuItem({
              to: '/admin/reports',
              label: t('nav.reports'),
              icon: 'reports',
              requiredRole: ['global_admin', 'assessment_admin', 'admin'],
            })}
          </div>

          {/* Settings Section */}
          <div className={`${collapsed ? 'space-y-1' : 'space-y-0'} ${collapsed ? 'mt-3' : 'mt-2'}`}>
            <p className={`px-3 py-2 text-xs font-bold text-eucnet-grey-500 uppercase tracking-wider ${collapsed ? 'md:hidden' : ''}`}>
              {t('nav.settings')}
            </p>
            {menuItem({
              to: '/profile',
              label: t('nav.profileSettings'),
              icon: 'profile',
              requiredRole: null,
            })}
            {role === 'global_admin' && menuItem({
              to: '/admin/settings',
              label: t('nav.systemSettings'),
              icon: 'settings',
              requiredRole: ['global_admin'],
            })}
          </div>
        </div>
      </div>
      </aside>
    </>
  );
}
